// assets/magnify-image.js

class MagnifyImage extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.magnifierSize = 150;
    this.zoomLevel = 2.5;
    this.isActive = false;
    this.isModalOpen = false;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
    this.createModal();
  }

  static get observedAttributes() {
    return ['src', 'alt', 'magnifier-size', 'zoom-level', 'disable-modal'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'magnifier-size':
          this.magnifierSize = parseInt(newValue) || 150;
          break;
        case 'zoom-level':
          this.zoomLevel = parseFloat(newValue) || 2.5;
          break;
      }
      this.render();
    }
  }

  render() {
    const src = this.getAttribute('src') || '';
    const alt = this.getAttribute('alt') || '';
    const disableModal = this.hasAttribute('disable-modal');
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          position: relative;
          cursor: ${disableModal ? 'crosshair' : 'zoom-in'};
          overflow: hidden;
        }

        .magnify-container {
          position: relative;
          display: inline-block;
          line-height: 0;
        }

        .magnify-image {
          display: block;
          max-width: 100%;
          height: auto;
          transition: opacity 0.3s ease;
        }

        .magnifier {
          position: absolute;
          border: 2px solid #fff;
          border-radius: 50%;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          background-repeat: no-repeat;
          background-color: #fff;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          pointer-events: none;
          z-index: 1000;
        }

        .magnifier.active {
          opacity: 1;
          visibility: visible;
        }
      </style>

      <div class="magnify-container">
        <img class="magnify-image" src="${src}" alt="${alt}" draggable="false">
        <div class="magnifier"></div>
      </div>
    `;
  }

  createModal() {
    // Create modal in document body to avoid z-index issues
    this.modal = document.createElement('div');
    this.modal.className = 'magnify-modal-overlay';
    
    const src = this.getAttribute('src') || '';
    const alt = this.getAttribute('alt') || '';
    
    this.modal.innerHTML = `
      <style>
        .magnify-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          cursor: zoom-out;
        }

        .magnify-modal-overlay.active {
          opacity: 1;
          visibility: visible;
        }

        .magnify-modal-content {
          position: relative;
          max-width: 95vw;
          max-height: 95vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .magnify-modal-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          transition: transform 0.3s ease;
          cursor: grab;
          transform-origin: center center;
        }

        .magnify-modal-image:active {
          cursor: grabbing;
        }

        .magnify-close-button {
          position: absolute;
          top: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
          color: #333;
          transition: all 0.2s ease;
          z-index: 10001;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .magnify-close-button:hover {
          background: rgba(255, 255, 255, 1);
          transform: scale(1.1);
        }

        .magnify-zoom-controls {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 12px;
          align-items: center;
          background: rgba(0, 0, 0, 0.8);
          padding: 12px 20px;
          border-radius: 25px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .magnify-zoom-btn {
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 6px;
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: bold;
          color: #333;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .magnify-zoom-btn:hover {
          background: rgba(255, 255, 255, 1);
          transform: scale(1.05);
        }

        .magnify-zoom-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .magnify-zoom-info {
          background: rgba(255, 255, 255, 0.95);
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 14px;
          color: #333;
          min-width: 50px;
          text-align: center;
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .magnify-modal-content {
            max-width: 100vw;
            max-height: 100vh;
            padding: 20px;
          }
          
          .magnify-close-button {
            top: 20px;
            right: 20px;
            background: rgba(255, 255, 255, 0.98);
            width: 44px;
            height: 44px;
            font-size: 22px;
          }
          
          .magnify-zoom-controls {
            bottom: 30px;
            background: rgba(0, 0, 0, 0.85);
            padding: 15px 25px;
            border-radius: 30px;
          }
          
          .magnify-zoom-btn {
            width: 44px;
            height: 44px;
            font-size: 20px;
            background: rgba(255, 255, 255, 0.98);
          }
          
          .magnify-zoom-info {
            background: rgba(255, 255, 255, 0.98);
            padding: 10px 15px;
            font-size: 16px;
          }
        }
      </style>
      
      <div class="magnify-modal-content">
        <img class="magnify-modal-image" src="${src}" alt="${alt}" draggable="false">
        <button class="magnify-close-button" title="Close (ESC)">&times;</button>
        <div class="magnify-zoom-controls">
          <button class="magnify-zoom-btn zoom-out" title="Zoom Out">−</button>
          <span class="magnify-zoom-info">100%</span>
          <button class="magnify-zoom-btn zoom-in" title="Zoom In">+</button>
          <button class="magnify-zoom-btn zoom-reset" title="Reset Zoom">⌂</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);
    this.setupModalEventListeners();
  }

  setupEventListeners() {
    const container = this.shadowRoot.querySelector('.magnify-container');
    const image = this.shadowRoot.querySelector('.magnify-image');
    const magnifier = this.shadowRoot.querySelector('.magnifier');

    // Wait for image to load before setting up magnifier
    image.addEventListener('load', () => {
      this.setupMagnifier();
    });

    // If image is already loaded
    if (image.complete) {
      this.setupMagnifier();
    }

    // Click to open modal (unless disabled)
    container.addEventListener('click', (e) => {
      if (!this.hasAttribute('disable-modal') && !this.hasAttribute('disabled')) {
        e.preventDefault();
        this.openModal();
      }
    });

    // Hover magnifier events
    container.addEventListener('mouseenter', (e) => {
      if (this.hasAttribute('disabled')) return;
      this.showMagnifier();
    });

    container.addEventListener('mouseleave', (e) => {
      this.hideMagnifier();
    });

    container.addEventListener('mousemove', (e) => {
      if (this.hasAttribute('disabled') || !this.isActive || this.isModalOpen) return;
      this.updateMagnifier(e);
    });

    // Touch events for mobile
    container.addEventListener('touchstart', (e) => {
      if (this.hasAttribute('disabled')) return;
      
      // Check for double tap for mobile modal opening
      const now = Date.now();
      if (this.lastTap && (now - this.lastTap) < 300) {
        if (!this.hasAttribute('disable-modal')) {
          e.preventDefault();
          this.openModal();
          return;
        }
      }
      this.lastTap = now;
      
      if (!this.isModalOpen) {
        e.preventDefault();
        this.showMagnifier();
        this.updateMagnifier(e.touches[0]);
      }
    });

    container.addEventListener('touchmove', (e) => {
      if (this.hasAttribute('disabled') || !this.isActive || this.isModalOpen) return;
      e.preventDefault();
      this.updateMagnifier(e.touches[0]);
    });

    container.addEventListener('touchend', (e) => {
      if (!this.isModalOpen) {
        e.preventDefault();
        this.hideMagnifier();
      }
    });
  }

// --- inside your class: replace the whole setupModalEventListeners() body with this ---
setupModalEventListeners() {
  const modalImage = this.modal.querySelector('.magnify-modal-image');
  const closeButton = this.modal.querySelector('.magnify-close-button');
  const zoomInBtn = this.modal.querySelector('.zoom-in');
  const zoomOutBtn = this.modal.querySelector('.zoom-out');
  const zoomResetBtn = this.modal.querySelector('.zoom-reset');
  const zoomInfo = this.modal.querySelector('.magnify-zoom-info');

  // Default zoom: 70% on desktop, 100% on smaller screens
  let currentZoom = window.innerWidth > 1024 ? 0.5 : 1;
  let isDragging = false;
  let dragStart = { x: 0, y: 0 };
  let imagePosition = { x: 0, y: 0 };

  // Close modal events
  this.modal.addEventListener('click', (e) => {
    if (e.target === this.modal) {
      this.closeModal();
    }
  });

  closeButton.addEventListener('click', () => {
    this.closeModal();
  });

  // Keyboard events
  const handleKeydown = (e) => {
    if (!this.isModalOpen) return;

    switch(e.key) {
      case 'Escape':
        this.closeModal();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  };

  document.addEventListener('keydown', handleKeydown);

  // Zoom controls
  const updateZoomDisplay = () => {
    zoomInfo.textContent = Math.round(currentZoom * 100) + '%';
    zoomOutBtn.disabled = currentZoom <= 0.5;
    zoomInBtn.disabled = currentZoom >= 3;
  };

  // helper to apply transform consistently
  const applyTransform = () => {
    modalImage.style.transform = `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${currentZoom})`;
    // adjust cursor based on zoom
    modalImage.style.cursor = currentZoom > 1 ? 'grab' : 'zoom-out';
  };

  this.zoomIn = () => {
    if (currentZoom < 3) {
      currentZoom = Math.min(3, currentZoom + 0.2);
      applyTransform();
      updateZoomDisplay();
    }
  };

  this.zoomOut = () => {
    if (currentZoom > 0.5) {
      currentZoom = Math.max(0.5, currentZoom - 0.2);
      applyTransform();
      updateZoomDisplay();
    }
  };

  this.resetZoom = () => {
    currentZoom = window.innerWidth > 1024 ? 0.5 : 1; // respect desktop default
    imagePosition = { x: 0, y: 0 };
    applyTransform();
    updateZoomDisplay();
  };

  zoomInBtn.addEventListener('click', this.zoomIn);
  zoomOutBtn.addEventListener('click', this.zoomOut);
  zoomResetBtn.addEventListener('click', this.resetZoom);

  // Mouse wheel zoom
  modalImage.addEventListener('wheel', (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  });

  // Dragging functionality
  modalImage.addEventListener('mousedown', (e) => {
    if (currentZoom > 1) {
      isDragging = true;
      dragStart = { x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y };
      modalImage.style.cursor = 'grabbing';
      e.preventDefault();
    }
  });

  document.addEventListener('mousemove', (e) => {
    if (isDragging) {
      imagePosition.x = e.clientX - dragStart.x;
      imagePosition.y = e.clientY - dragStart.y;
      applyTransform();
      e.preventDefault();
    }
  });

  document.addEventListener('mouseup', () => {
    if (isDragging) {
      isDragging = false;
      modalImage.style.cursor = currentZoom > 1 ? 'grab' : 'zoom-out';
    }
  });

  // Touch dragging
  let touchStart = null;

  modalImage.addEventListener('touchstart', (e) => {
    if (currentZoom > 1 && e.touches.length === 1) {
      touchStart = {
        x: e.touches[0].clientX - imagePosition.x,
        y: e.touches[0].clientY - imagePosition.y
      };
      e.preventDefault();
    }
  });

  modalImage.addEventListener('touchmove', (e) => {
    if (touchStart && e.touches.length === 1) {
      imagePosition.x = e.touches[0].clientX - touchStart.x;
      imagePosition.y = e.touches[0].clientY - touchStart.y;
      applyTransform();
      e.preventDefault();
    }
  });

  modalImage.addEventListener('touchend', () => {
    touchStart = null;
  });

  // Initialize zoom display & transform (this is the key so modal shows at 70%).
  updateZoomDisplay();
  applyTransform();

  // Store cleanup function
  this.modalCleanup = () => {
    document.removeEventListener('keydown', handleKeydown);
    currentZoom = 1;
    imagePosition = { x: 0, y: 0 };
  };
}

  setupMagnifier() {
    const image = this.shadowRoot.querySelector('.magnify-image');
    const magnifier = this.shadowRoot.querySelector('.magnifier');
    
    if (!image.src) return;

    // Set magnifier size
    magnifier.style.width = `${this.magnifierSize}px`;
    magnifier.style.height = `${this.magnifierSize}px`;

    // Set background image for magnifier
    magnifier.style.backgroundImage = `url(${image.src})`;
    magnifier.style.backgroundSize = `${image.offsetWidth * this.zoomLevel}px ${image.offsetHeight * this.zoomLevel}px`;
  }

  showMagnifier() {
    if (this.isModalOpen) return;
    const magnifier = this.shadowRoot.querySelector('.magnifier');
    magnifier.classList.add('active');
    this.isActive = true;
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('magnifier-show', {
      bubbles: true,
      detail: { active: true }
    }));
  }

  hideMagnifier() {
    const magnifier = this.shadowRoot.querySelector('.magnifier');
    magnifier.classList.remove('active');
    this.isActive = false;
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('magnifier-hide', {
      bubbles: true,
      detail: { active: false }
    }));
  }

  updateMagnifier(e) {
    const container = this.shadowRoot.querySelector('.magnify-container');
    const image = this.shadowRoot.querySelector('.magnify-image');
    const magnifier = this.shadowRoot.querySelector('.magnifier');

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate magnifier position
    const magnifierX = x - this.magnifierSize / 2;
    const magnifierY = y - this.magnifierSize / 2;

    // Keep magnifier within image bounds
    const maxX = image.offsetWidth - this.magnifierSize;
    const maxY = image.offsetHeight - this.magnifierSize;
    
    const clampedX = Math.max(0, Math.min(magnifierX, maxX));
    const clampedY = Math.max(0, Math.min(magnifierY, maxY));

    // Position magnifier
    magnifier.style.left = `${clampedX}px`;
    magnifier.style.top = `${clampedY}px`;

    // Calculate background position for zoom effect
    const backgroundX = -x * this.zoomLevel + this.magnifierSize / 2;
    const backgroundY = -y * this.zoomLevel + this.magnifierSize / 2;

    magnifier.style.backgroundPosition = `${backgroundX}px ${backgroundY}px`;

    // Dispatch custom event with position data
    this.dispatchEvent(new CustomEvent('magnifier-move', {
      bubbles: true,
      detail: { 
        x: x, 
        y: y, 
        magnifierX: clampedX, 
        magnifierY: clampedY 
      }
    }));
  }

openModal() {
  this.isModalOpen = true;
  this.hideMagnifier();

  // Update modal image src if changed
  const modalImage = this.modal.querySelector('.magnify-modal-image');
  const currentSrc = this.getAttribute('src') || '';
  const currentAlt = this.getAttribute('alt') || '';

  modalImage.src = currentSrc;
  modalImage.alt = currentAlt;

  // Ensure modal opens at the default zoom for the device
  if (this.resetZoom) this.resetZoom();

  this.modal.classList.add('active');
  document.body.style.overflow = 'hidden';

  // Dispatch custom event
  this.dispatchEvent(new CustomEvent('modal-open', {
    bubbles: true,
    detail: { isOpen: true }
  }));
}

  closeModal() {
    this.isModalOpen = false;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    
    // Reset zoom
    if (this.resetZoom) {
      this.resetZoom();
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('modal-close', {
      bubbles: true,
      detail: { isOpen: false }
    }));
  }

  disconnectedCallback() {
    // Clean up modal when component is removed
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
    if (this.modalCleanup) {
      this.modalCleanup();
    }
  }

  // Public methods
  enable() {
    this.removeAttribute('disabled');
  }

  disable() {
    this.setAttribute('disabled', '');
    this.hideMagnifier();
    this.closeModal();
  }

  updateImage(src, alt = '') {
    this.setAttribute('src', src);
    if (alt) this.setAttribute('alt', alt);
    
    // Update modal image if it exists
    if (this.modal) {
      const modalImage = this.modal.querySelector('.magnify-modal-image');
      if (modalImage) {
        modalImage.src = src;
        modalImage.alt = alt;
      }
    }
  }

  setZoomLevel(level) {
    this.zoomLevel = parseFloat(level) || 2.5;
    this.setupMagnifier();
  }

  setMagnifierSize(size) {
    this.magnifierSize = parseInt(size) || 150;
    this.setupMagnifier();
  }

  enableModal() {
    this.removeAttribute('disable-modal');
    this.render();
  }

  disableModal() {
    this.setAttribute('disable-modal', '');
    this.closeModal();
    this.render();
  }
}

// Register the custom element
customElements.define('magnify-image', MagnifyImage);